import { useState } from "react";
import axios from "axios";
import { ThumbsUp, ThumbsDown, MessageSquare, X } from "lucide-react";
import { toast } from "../hooks/use-toast";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Generate a unique client token for this browser
const getClientToken = () => {
  let token = localStorage.getItem("client_review_token");
  if (!token) {
    token = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("client_review_token", token);
  }
  return token;
};

const ReviewWidget = ({ codeId, codeName, codeNumber }) => {
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [status, setStatus] = useState(""); // "working" or "not_working"
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitReview = async () => {
    if (!status) {
      toast({
        title: "Error",
        description: "Please select if the code is working or not",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);
      const clientToken = getClientToken();

      await axios.post(`${API}/codes/${codeId}/review`, {
        status,
        comment: comment.trim(),
        client_token: clientToken,
      });

      toast({
        title: "Thank you!",
        description: "Your review has been submitted successfully",
      });

      // Reset form
      setShowReviewForm(false);
      setStatus("");
      setComment("");
    } catch (error) {
      console.error("Error submitting review:", error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit review",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="review-widget">
      {!showReviewForm ? (
        <button
          onClick={() => setShowReviewForm(true)}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
        >
          <MessageSquare className="w-4 h-4" />
          Leave a review
        </button>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900">Review: {codeName}</h4>
            <button
              onClick={() => {
                setShowReviewForm(false);
                setStatus("");
                setComment("");
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-3">
            Code: <span className="font-mono font-semibold">{codeNumber}</span>
          </p>

          {/* Status Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Does this code work?
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setStatus("working")}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg border-2 transition-all ${
                  status === "working"
                    ? "border-green-500 bg-green-50 text-green-700"
                    : "border-gray-300 hover:border-green-300"
                }`}
              >
                <ThumbsUp className="w-5 h-5" />
                <span className="font-medium">Working</span>
              </button>
              <button
                onClick={() => setStatus("not_working")}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg border-2 transition-all ${
                  status === "not_working"
                    ? "border-red-500 bg-red-50 text-red-700"
                    : "border-gray-300 hover:border-red-300"
                }`}
              >
                <ThumbsDown className="w-5 h-5" />
                <span className="font-medium">Not Working</span>
              </button>
            </div>
          </div>

          {/* Comment */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional comments (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience with this code..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows="3"
              maxLength="500"
            />
            <p className="text-xs text-gray-500 mt-1">
              {comment.length}/500 characters
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex gap-2">
            <button
              onClick={handleSubmitReview}
              disabled={submitting || !status}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewWidget;
